import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import './RankMatchWaiting.css';
import QuoteRotator from "../QuoteRotator/QuoteRotator";
import {API_BASE, WS_BASE} from '../../utils/apiConfig';
import {RANK_MATCH_SEARCH_SUPERSEDED_CLOSE_CODE} from '../../configs/rankMatch';
import {getRankMatchOpponentCountsSummary} from '../../utils/lobby/getRankMatchOpponentCountsSummary';
import {RANKED_SEARCH_HINT_STORAGE_KEY, useDriftingSearchHint} from '../../hooks/other/useDriftingSearchHint';
import {
  clearExpectingClose,
  markExpectingClose,
  triggerConnectionLost,
  triggerReconnectStatus
} from '../../utils/connectionMonitor';
import {RANK_MATCH_JOIN_OUTCOMES, requestRankMatchJoin} from '../../utils/lobby/requestRankMatchJoin';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import {sendNetworkEvent} from '../../analytics/gameAnalytics';
import {ALL_FACTION_SKINS, getFactionSkinImagePath} from '../../configs/factions/skins/allFactionSkins';
import {getFactionImagePath} from '../../utils/factions/getFactionImagePath';
import {getFactionRankImagePath} from '../../utils/factions/getFactionRankImagePath';
import {checkIfRankSkinArtExists, getRankSkinId, RANK_SKIN_LEAGUES} from '../../configs/rankSkins';
import {getRandomElement} from '../../utils/other/getRandomElement';
import {MENU_SOUND_MANAGER} from '../../scenes/MenuSoundManager';
import MaskedIcon from '../coloredIcons/MaskedIcon/MaskedIcon';

const DEFAULT_BUST_IMAGE = '/img/other/strategikon_bust.png';

// Сторож тишины. Сервер шлет сокету ожидания PING каждые 10 секунд (см.
// tickRankMatchWaitingHeartbeat в server/index.js). При молчаливом обрыве
// (пропала мобильная сеть) close не приходит, и игрок смотрел бы на поиск,
// который на сервере давно снят
export const RANK_MATCH_SERVER_SILENCE_LIMIT_MS = 25000;
const SILENCE_CHECK_INTERVAL_MS = 2500;

// Попытки продолжить поиск после обрыва сокета ожидания: первая сразу и без
// оверлея, дальше — с растущей паузой и оверлеем «Переподключение». Исчерпаны —
// оверлей обрыва связи
export const RANK_MATCH_RESUME_DELAYS_MS = [0, 1000, 2000, 4000, 8000];

// Вкладка пробыла в фоне дольше — по возвращении поиск сразу продолжается
// повторным входом, не дожидаясь сторожа тишины: мобильный браузер в фоне
// может не отвечать на пинг, и сервер снимает такой сокет через 30-40 с
export const RANK_MATCH_RESUME_AFTER_HIDDEN_MS = 20000;

// Случайный скин (включая обычный образ фракции без скина и рейтинговые
// скины, у которых есть арт, см. getFactionSkinDisplayProps) той фракции,
// чья цитата сейчас показывается в QuoteRotator — чисто декоративный подбор.
// notObtainable (dev/alpha/beta) исключены — эти скины не бывают ни у одного
// реального игрока, показывать их тут нечестно (как и при подборе скина
// ботам, см. pickBotFactionSkin на сервере).
// isRealQuote — цитата реальной исторической личности (см. QuoteRotator):
// у нее не должно быть фракционного портрета, даже если factionId указан
function pickRandomFactionImage(factionId, isRealQuote) {
  if (isRealQuote || !factionId) return DEFAULT_BUST_IMAGE;
  const factionSkins = ALL_FACTION_SKINS.filter(
    (skin) => skin.factionId === factionId && !skin.notObtainable
  );
  const options = [
    null,
    ...factionSkins,
    // Рейтинговые — только те, у которых есть арт: алмазного у
    // нереализованных фракций нет, а цитаты бывают и их
    ...RANK_SKIN_LEAGUES
      .filter((rank) => checkIfRankSkinArtExists(rank, factionId))
      .map((rank) => ({id: getRankSkinId(factionId, rank), imagePath: getFactionRankImagePath(factionId, rank)})),
  ];
  const picked = getRandomElement(options);
  if (!picked) return getFactionImagePath(factionId);
  return picked.imagePath || getFactionSkinImagePath(picked.id);
}

/**
 * Экран ожидания автоматического подбора рейтингового матча. По составу
 * аналогичен LoadingScreen, но вместо тулбара загрузки показывает статистику
 * поиска. Держит отдельное (не networkSocket.js) вебсокет-соединение с
 * сервером типа rankMatchWaiting — сервер по нему присылает обновления
 * размера пула ожидания и сигнал готовности лобби (переход на LobbyPage).
 * При размонтировании соединение закрывается — сервер удаляет игрока из пула.
 *
 * Обрыв сокета (сеть, долгий фон на мобильном, сервер снял молчащий сокет)
 * поиск не прекращает: экран сам входит в поиск заново — сервер перехватывает
 * прежнюю запись игрока, место в очереди сохраняется, пока запись жива, — и
 * открывает новый сокет. Оверлей обрыва связи — только если это не удалось.
 *
 * Ограничение длительности матча (opponentCounts, configs/rankMatch.js) задано
 * на экране настройки поиска (RankMatchSetup) и здесь только показывается:
 * менять условия подбора во время поиска нельзя — только отменить поиск
 * (onCancel возвращает на экран настройки).
 */
function RankMatchWaiting({playerId, searchId, onLobbyReady, onCancel, onSearchSuperseded, opponentCounts}) {
  usePageAnalytics('RankMatchWaiting');

  const auth = useSelector((state) => state.private.auth);
  const isSuperAdmin = Boolean(auth.isSuperAdmin);

  // Детальный ростер пула ожидания (только для суперадмина, см. сервер —
  // остальным он не рассылается вовсе). Скрыт по умолчанию, тоглится
  // двойным кликом/тапом по заголовку "Поиск матча" (handleTitleDoubleClick)
  const [isPoolRosterVisible, setIsPoolRosterVisible] = useState(false);
  const [poolRoster, setPoolRoster] = useState([]);

  const [waitingSize, setWaitingSize] = useState(1);
  const [waitedSeconds, setWaitedSeconds] = useState(0);
  const [factionImage, setFactionImage] = useState(DEFAULT_BUST_IMAGE);
  // Портрет фракции затухает/появляется синхронно с цитатой (тот же
  // isQuoteVisible из QuoteRotator, см. onVisibilityChange) — src меняется
  // на уже смонтированном <img>, поэтому переключение идет плавно по
  // opacity, а не мгновенно
  const [isImageVisible, setIsImageVisible] = useState(true);
  // Прелоад нового портрета (handleFactionChange) и сигнал "пора быть
  // видимым" (handleImageVisibilityChange, от цитаты) приходят независимо —
  // картинка раскрывается только когда ОБА готовы, какое бы из двух событий
  // ни случилось последним. Без этого при незакэшированной/медленной
  // загрузке src уже применялся, а opacity уже возвращался к 1 ДО того, как
  // браузер закончил decode нового изображения — на месте нового портрета
  // на миг был виден старый (тот же mounted-элемент еще не перерисован),
  // который затем резко подменялся новым
  const currentPreloadRef = useRef(null);
  const isVisibleSignalRef = useRef(true);
  // Ключ перезапускает CSS-анимацию пульса счетчика соперников (пересоздает
  // span при каждом росте счетчика — надежнее переключения класса на одном узле)
  const [enemyFoundPulseKey, setEnemyFoundPulseKey] = useState(0);
  const averageWaitSeconds = useDriftingSearchHint(RANKED_SEARCH_HINT_STORAGE_KEY, 30, 55, 1, 5);
  const mountedRef = useRef(true);
  const prevOpponentsFoundRef = useRef(0);
  // Колбэк перехвата поиска — через ref, чтобы смена его идентичности не
  // пересоздавала сокет ожидания
  const onSearchSupersededRef = useRef(onSearchSuperseded);
  onSearchSupersededRef.current = onSearchSuperseded;
  // Идентификатор текущего поиска: после продолжения поиска он новый
  const searchIdRef = useRef(searchId);
  const playerNameRef = useRef(auth.name);
  playerNameRef.current = auth.name;

  // Раскрывает портрет: применяет src и делает видимым за один рендер (без
  // этого смена src на уже видимом <img> дала бы мгновенный, а не плавный
  // переход — см. комментарий у currentPreloadRef)
  const revealImage = useCallback((src) => {
    if (!mountedRef.current) return;
    setFactionImage(src);
    setIsImageVisible(true);
  }, []);

  const handleFactionChange = useCallback((factionId, isRealQuote) => {
    const nextImageSrc = pickRandomFactionImage(factionId, isRealQuote);
    const preloadImage = new Image();
    currentPreloadRef.current = preloadImage;
    const onSettled = () => {
      // Сюда же попадаем и при ошибке загрузки (onerror) — раскрываем то,
      // что есть, лучше не оставлять портрет застрявшим невидимым навсегда
      if (currentPreloadRef.current !== preloadImage) return; // сменилось новой ротацией цитат
      if (isVisibleSignalRef.current) {
        revealImage(nextImageSrc);
      }
    };
    preloadImage.onload = onSettled;
    preloadImage.onerror = onSettled;
    preloadImage.src = nextImageSrc;
  }, [revealImage]);

  // От QuoteRotator: true — цитата (и портрет) снова должны быть видимы.
  // Если прелоад текущего портрета уже готов — раскрываем сразу; иначе просто
  // запоминаем сигнал, и раскроет его сам preloadImage.onload/onerror выше,
  // когда доготовится (какое из двух событий не случится последним)
  const handleImageVisibilityChange = useCallback((visible) => {
    isVisibleSignalRef.current = visible;
    if (!visible) {
      if (mountedRef.current) {
        setIsImageVisible(false);
      }
      return;
    }
    const preloadImage = currentPreloadRef.current;
    if (preloadImage && preloadImage.complete) {
      revealImage(preloadImage.src);
    }
  }, [revealImage]);

  // Явная отмена поиска: удаляем себя из пула ожидания на сервере СРАЗУ
  // (REST, без грейс-периода переподключения — иначе могло бы затянуть в
  // собирающееся лобби) и возвращаемся в главное меню. WS-соединение закроет
  // cleanup эффекта при размонтировании
  const handleCancel = useCallback(() => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    fetch(`${API_BASE}/rank-match/leave`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      // searchId — чтобы отмена не сняла поиск, уже перехваченный другой вкладкой
      body: JSON.stringify({playerId, searchId: searchIdRef.current}),
    }).catch((error) => {
      // Даже если запрос не дошел, из пула игрока уберет закрытие WS по
      // грейс-периоду — уходим в меню в любом случае
      console.error('[RankMatchWaiting] Failed to leave rank match pool', error);
    });
    onCancel?.();
  }, [playerId, onCancel]);

  // Двойной клик/тап по заголовку "Поиск матча" — тоглит видимость ростера
  // пула ожидания. Только для суперадмина (обычным игрокам сервер сам не
  // присылает состав пула, так что показывать тут все равно нечего)
  const handleTitleDoubleClick = useCallback(() => {
    if (!isSuperAdmin) return;
    setIsPoolRosterVisible((visible) => !visible);
  }, [isSuperAdmin]);

  useEffect(() => {
    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      setWaitedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Эффект снят (уход с экрана): закрытия его сокетов — не обрыв. Свой флаг,
    // а не глобальный markExpectingClose: тот общий для всех сокетов
    // приложения и может быть сброшен чужим onopen раньше, чем придет close
    let isEffectClosed = false;
    let ws = null;
    let silenceIntervalId = null;
    let lastServerMessageAt = Date.now();
    let hiddenAt = null;
    let resumeAttempts = 0;
    let isResumeInProgress = false;
    let resumeTimeoutId = null;

    const stopSilenceWatchdog = () => {
      clearInterval(silenceIntervalId);
      silenceIntervalId = null;
    };

    // Сокет брошен нами: его закрытие и поздние сообщения ничего не значат (в
    // том числе RANK_MATCH_SEARCH_SUPERSEDED — это наш же повторный вход)
    const abandonSocket = () => {
      stopSilenceWatchdog();
      if (!ws) return;
      ws.isAbandoned = true;
      ws.close();
      ws = null;
    };

    const giveUpResume = () => {
      triggerReconnectStatus(null);
      // Флаг «закрытие ожидаемо» мог остаться взведенным с другого экрана —
      // тогда оверлей обрыва молча не показался бы
      clearExpectingClose();
      triggerConnectionLost();
    };

    const resumeSearch = (reason) => {
      if (isEffectClosed || isResumeInProgress) return;
      abandonSocket();
      if (resumeAttempts >= RANK_MATCH_RESUME_DELAYS_MS.length) {
        giveUpResume();
        return;
      }

      const delay = RANK_MATCH_RESUME_DELAYS_MS[resumeAttempts];
      resumeAttempts += 1;
      isResumeInProgress = true;
      if (resumeAttempts === 1) {
        console.warn(`[RankMatchWaiting] resuming search: ${reason}`);
        sendNetworkEvent('rank_match_search_resume', reason);
      } else {
        triggerReconnectStatus({
          attempt: resumeAttempts,
          maxAttempts: RANK_MATCH_RESUME_DELAYS_MS.length,
          retryInMs: delay,
        });
      }

      const attemptResume = async () => {
        resumeTimeoutId = null;
        const outcome = await requestRankMatchJoin({playerName: playerNameRef.current, playerId});
        isResumeInProgress = false;
        if (isEffectClosed) return;

        if (outcome.kind === RANK_MATCH_JOIN_OUTCOMES.WAITING) {
          if (outcome.searchId) {
            searchIdRef.current = outcome.searchId;
          }
          connect();
        } else if (outcome.kind === RANK_MATCH_JOIN_OUTCOMES.LOBBY) {
          // Лобби успело собраться, а сигнал готовности ушел в оборванный сокет
          triggerReconnectStatus(null);
          onLobbyReady(outcome.lobbyId);
        } else if (outcome.kind === RANK_MATCH_JOIN_OUTCOMES.FAILED) {
          resumeSearch(outcome.reason);
        } else {
          giveUpResume();
        }
      };
      if (delay > 0) {
        resumeTimeoutId = setTimeout(attemptResume, delay);
      } else {
        attemptResume();
      }
    };

    function connect() {
      const socket = new WebSocket(`${WS_BASE}?type=rankMatchWaiting&playerId=${encodeURIComponent(playerId)}`);
      ws = socket;
      let isSearchSuperseded = false;
      // Сервер, выдавший searchId, шлет PING — сторож взводится сразу: сеть
      // может умереть и до первого PING. Сервер без пульса (searchId нет)
      // молчит законно, пока пул не меняется, — там сторож ждет первого PING
      let isSilenceWatchdogArmed = Boolean(searchIdRef.current);
      lastServerMessageAt = Date.now();

      stopSilenceWatchdog();
      silenceIntervalId = setInterval(() => {
        if (!isSilenceWatchdogArmed || socket.readyState !== WebSocket.OPEN) return;
        const silenceMs = Date.now() - lastServerMessageAt;
        if (silenceMs < RANK_MATCH_SERVER_SILENCE_LIMIT_MS) return;

        // Не ждем onclose: у мертвого канала рукопожатие закрытия может не
        // завершиться долго
        console.warn(`[RankMatchWaiting] no data from server for ${silenceMs}ms`);
        sendNetworkEvent('rank_match_waiting_silence');
        resumeSearch('silence');
      }, SILENCE_CHECK_INTERVAL_MS);

      socket.onopen = () => {
        if (socket.isAbandoned || isEffectClosed) return;
        clearExpectingClose();
        lastServerMessageAt = Date.now();
        if (resumeAttempts > 0) {
          triggerReconnectStatus(null);
        }
      };

      socket.onmessage = (event) => {
        if (socket.isAbandoned || isEffectClosed) return;
        lastServerMessageAt = Date.now();
        // Сервер отвечает по новому сокету — поиск восстановлен
        resumeAttempts = 0;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'PING') {
            isSilenceWatchdogArmed = true;
          } else if (msg.type === 'RANK_MATCH_SEARCH_SUPERSEDED') {
            isSearchSuperseded = true;
          } else if (msg.type === 'RANK_MATCH_STATUS') {
            const newOpponentsFound = Math.max(0, msg.payload.waitingSize - 1);
            if (newOpponentsFound > prevOpponentsFoundRef.current) {
              MENU_SOUND_MANAGER.playRankEnemyFound();
              setEnemyFoundPulseKey((key) => key + 1);
            }
            prevOpponentsFoundRef.current = newOpponentsFound;
            setWaitingSize(msg.payload.waitingSize);
          } else if (msg.type === 'RANK_MATCH_POOL_ROSTER') {
            // Приходит только суперадмину (сервер не шлёт это обычным игрокам)
            setPoolRoster(msg.payload.users || []);
          } else if (msg.type === 'RANK_MATCH_LOBBY_READY') {
            MENU_SOUND_MANAGER.playRankLobbyStart();
            onLobbyReady(msg.payload.lobbyId);
          }
        } catch (e) {
          console.error('[RankMatchWaiting] WS message parse error', e);
        }
      };

      socket.onerror = (err) => {
        console.error('[RankMatchWaiting] WS error', err);
      };

      socket.onclose = (event) => {
        if (socket.isAbandoned || isEffectClosed) return;
        stopSilenceWatchdog();
        ws = null;
        // Тот же игрок начал поиск в другой вкладке: продолжать здесь — значит
        // отнимать поиск у той вкладки
        if (isSearchSuperseded || event.code === RANK_MATCH_SEARCH_SUPERSEDED_CLOSE_CODE) {
          triggerReconnectStatus(null);
          onSearchSupersededRef.current?.();
          return;
        }
        resumeSearch(`close_${event.code}`);
      };
    }

    // Вкладка вернулась из фона: таймеры там стояли, а накопившиеся сообщения
    // еще не разобраны — отсчет тишины начинается заново, иначе сторож принял
    // бы паузу фона за мертвый канал. После долгого фона сокет, скорее всего,
    // уже снят сервером или умер молча — поиск продолжается сразу
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      const hiddenMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      lastServerMessageAt = Date.now();
      if (hiddenMs >= RANK_MATCH_RESUME_AFTER_HIDDEN_MS) {
        resumeSearch('background');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    connect();

    return () => {
      isEffectClosed = true;
      clearTimeout(resumeTimeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (resumeAttempts > 0) {
        triggerReconnectStatus(null);
      }
      mountedRef.current = false;
      markExpectingClose();
      abandonSocket();
    };
  }, [playerId, onLobbyReady]);

  const opponentsFound = Math.max(0, waitingSize - 1);

  // Сводка ограничения длительности; null — без ограничений (плашки нет)
  const opponentCountsSummary = getRankMatchOpponentCountsSummary(opponentCounts);

  return (
    // Верх (портрет и цитата) прибит к верху экрана с постоянным отступом —
    // портрет не прыгает от длины цитаты; панель поиска прижата к низу, все
    // свободное место между ними. Размер портрета — от высоты экрана, чтобы
    // экран помещался без прокрутки (см. RankMatchWaiting.css)
    <div className="rank-match-waiting">
      <div className="rank-match-waiting_top">
        {/* Без key={factionImage}: смена src на том же смонтированном <img>
            не сбрасывает его отрендеренные размеры на время загрузки нового
            изображения (в отличие от remount по ключу) — раньше именно
            remount обнулял высоту бюста на долю секунды между угасанием
            старой цитаты и появлением новой, из-за чего цитата прыгала
            наверх экрана, пока новое фото не догружалось.
            opacity управляется извне (isImageVisible, синхронизирован с
            isQuoteVisible цитаты) — переключение портрета плавное, как у
            самой цитаты, а не мгновенное */}
        <img
          className={'rank-match-waiting-bust'}
          src={factionImage}
          alt={''}
          loading="eager"
          decoding="async"
          style={{opacity: isImageVisible ? 1 : 0}}
        />
        <QuoteRotator
          className={'rank-match-waiting_quote-rotator'}
          onFactionChange={handleFactionChange}
          onVisibilityChange={handleImageVisibilityChange}
        />
      </div>

      {/* На широком экране — одна строка: заголовок, показатели, отмена;
          ограничение длительности строкой ниже. На телефоне — колонкой, кнопка
          отмены последней */}
      <div className="rank-match-waiting-panel">
        <h1
          className="loading-screen-title rank-match-waiting-title"
          onDoubleClick={handleTitleDoubleClick}
        >Поиск матча</h1>
        <div className="rank-match-waiting-stats">
          <div className="rank-match-waiting-stat">
            <span className="rank-match-waiting-stat_label">Найдено соперников</span>
            <span className="rank-match-waiting-stat_value rank-match-waiting-stat_value_opponents">
              <span
                key={enemyFoundPulseKey}
                className={`rank-match-enemy-count${enemyFoundPulseKey > 0 ? ' rank-match-enemy-count_pulse' : ''}`}
              >{opponentsFound}</span>
              <MaskedIcon
                src="/img/icons/bust.png"
                color="var(--crystals-color)"
                className="rank-match-waiting-opponents-icon"
              />
            </span>
          </div>
          <div className="rank-match-waiting-stat">
            <span className="rank-match-waiting-stat_label">Время ожидания</span>
            <span className="rank-match-waiting-stat_value text-attention">{waitedSeconds} сек</span>
          </div>
          <div className="rank-match-waiting-stat">
            <span className="rank-match-waiting-stat_label">Среднее время ожидания</span>
            <span className="rank-match-waiting-stat_value text-attention">{averageWaitSeconds} сек</span>
          </div>
        </div>
        <button className="rank-match-cancel-button" onClick={handleCancel}>
          {/* Маска вместо <img>: иконка тонируется в цвет текста кнопки */}
          <span className="rank-match-cancel-button_icon" aria-hidden="true"/>
          Отменить поиск
        </button>
        {isSuperAdmin && isPoolRosterVisible && (
          <div className="rank-match-pool-roster">
            <div className="rank-match-pool-roster-title">{`Пул ожидания (${poolRoster.length})`}</div>
            <div className="rank-match-pool-roster-list">
              {poolRoster.length === 0 ? (
                <span className="rank-match-pool-roster-empty">Пусто</span>
              ) : (
                poolRoster.map((user) => (
                  <div key={user.id} className="rank-match-pool-roster-item">
                    {user.isBot && (
                      <img
                        className="rank-match-pool-roster-item-bot-icon"
                        src="/img/icons/bot.png"
                        alt="Бот"
                        title="Бот"
                      />
                    )}
                    <span className="rank-match-pool-roster-item-name">{user.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {/* Ограничение длительности только показывается: менять его во время
            поиска нельзя (выбор — на экране настройки поиска). Без ограничения
            строки нет */}
        {opponentCountsSummary && (
          <div className="rank-match-opponent-counts-plate">
            <span className="rank-match-opponent-counts-plate_value">{opponentCountsSummary.opponentsText}</span>
            <span className="rank-match-opponent-counts-plate_value">{opponentCountsSummary.durationText}</span>
            <span className="rank-match-opponent-counts-plate_warning text-attention">
              Время ожидания может быть увеличено
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default RankMatchWaiting;
